import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Building2, Mail, Star, KeyRound, CheckCircle2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { BRAND_PHOTOS, AW_LOGO } from '../lib/brand';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('manager');
  const [loading, setLoading] = useState(false);
  const [preregisteredUser, setPreregisteredUser] = useState(null);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingApproval, setPendingApproval] = useState(location.state?.pendingApproval || null);

  const checkEmail = async () => {
    if (!email) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/check-email`, { email: email.toLowerCase().trim() });
      if (response.data.needs_password) {
        setPreregisteredUser(response.data);
        setName(response.data.name || '');
        setMode('setPassword');
        toast.info('Welcome! Please set your password to continue');
      }
    } catch (error) {}
    finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setResetEmailSent(true);
      toast.success('Reset link sent to your email!');
    } catch (error) { toast.error('Failed to send reset email'); }
    finally { setLoading(false); }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/set-password`, { email, password, name: name || preregisteredUser?.name });
      
      if (res.data.auto_approved) {
        // Auto-login for owners/area managers
        toast.success('Password set! Logging you in...');
        try {
          const user = await login(email, password);
          toast.success(`Welcome, ${user.name}!`);
          navigate('/select-department');
          return;
        } catch {
          // Fall back to login screen
          toast.success('Password set! Please log in.');
        }
      } else {
        toast.success(
          <div>
            <p className="font-semibold">You're all set!</p>
            <p className="text-sm">Your account is pending approval. We'll notify you once approved.</p>
          </div>,
          { duration: 6000 }
        );
      }
      
      setMode('login');
      setPassword('');
      setConfirmPassword('');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to set password'); }
    finally { setLoading(false); }
  };

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/select-department';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        // ALWAYS check email first to detect pre-registered users needing password
        try {
          const checkRes = await axios.post(`${API_URL}/auth/check-email`, { email: email.toLowerCase() });
          if (checkRes.data.needs_password) {
            setPreregisteredUser(checkRes.data);
            setName(checkRes.data.name || '');
            setMode('setPassword');
            toast.info('Welcome! Please set your password to get started');
            setLoading(false);
            return;
          }
          if (!checkRes.data.exists) {
            toast.error('Email not found. Please register or check your email.');
            setLoading(false);
            return;
          }
        } catch (checkErr) {
          // If check-email fails, continue with login attempt
        }

        const user = await login(email.toLowerCase(), password);
        toast.success(`Welcome back, ${user.name}!`);
        navigate(['owner', 'area_manager', 'director', 'admin'].includes(user.role) ? '/overview' : '/home');
      } else if (mode === 'register') {
        const result = await register(email.toLowerCase(), password, name, role);
        if (result.pending_approval) {
          toast.info(<div><p className="font-semibold">Almost there!</p><p className="text-sm">Your account needs approval. We'll be in touch!</p></div>, { duration: 6000 });
          setMode('login');
          setEmail(''); setPassword(''); setName('');
        } else {
          toast.success('Welcome aboard!');
          navigate('/select-department');
        }
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Authentication failed';
      const status = error.response?.status;
      
      if (status === 412 || msg === 'PASSWORD_NOT_SET') {
        try {
          const checkRes = await axios.post(`${API_URL}/auth/check-email`, { email: email.toLowerCase() });
          if (checkRes.data.needs_password) {
            setPreregisteredUser(checkRes.data);
            setName(checkRes.data.name || '');
            setMode('setPassword');
            toast.info('Welcome! Please set your password to get started');
          }
        } catch { 
          setMode('setPassword');
          toast.info('Please set your password to continue');
        }
      } else if (status === 403 || (msg && msg.includes('pending'))) {
        toast.warning('Your account is pending approval. You will be notified once approved.');
      } else {
        toast.error(msg);
      }
    }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-12 bg-[#FDFBF7]" data-testid="login-page">
      {/* Left Panel - Brand photo */}
      <div className="hidden lg:flex lg:col-span-7 relative overflow-hidden grain">
        <img
          src={BRAND_PHOTOS.front}
          alt="Elmwood A&W restaurant at golden hour"
          className="absolute inset-0 w-full h-full object-cover object-center animate-ken-burns"
          data-testid="login-hero-photo"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#3E2418]/90 via-[#3E2418]/35 to-black/5" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#3E2418]/30 to-transparent" />

        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 text-white w-full reveal">
          <div className="flex items-center gap-4">
            <img src={AW_LOGO} alt="A&W" className="w-14 h-14 object-contain drop-shadow-lg" />
            <div>
              <h1 className="text-2xl font-black tracking-tight leading-none" style={{ fontFamily: 'Chivo, sans-serif' }}>Elmwood Management</h1>
              <p className="text-orange-200/90 text-[11px] uppercase tracking-[0.25em] mt-1.5 font-semibold">A&amp;W Canada Franchisee</p>
            </div>
          </div>

          <div className="max-w-xl space-y-8">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-orange-300 font-semibold mb-4">Operations Hub</p>
              <h2 className="text-4xl xl:text-5xl font-black leading-[1.05] tracking-tight drop-shadow-md" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Run every restaurant<br />
                <span className="text-orange-400">like your best one.</span>
              </h2>
              <p className="text-white/80 text-base mt-5 max-w-md leading-relaxed">
                Daily checklists, live KPIs, audits and Rooty AI — one place for the whole Elmwood team.
              </p>
            </div>

            <div className="glass-dark rounded-2xl p-5 grid grid-cols-3 divide-x divide-white/15 shadow-2xl max-w-md" data-testid="login-stats">
              <div className="pr-4">
                <p className="text-3xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>10</p>
                <p className="text-[11px] uppercase tracking-wider text-white/70 mt-1">Restaurants</p>
              </div>
              <div className="px-4">
                <p className="text-3xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>2</p>
                <p className="text-[11px] uppercase tracking-wider text-white/70 mt-1">Provinces</p>
              </div>
              <div className="pl-4">
                <p className="text-3xl font-black flex items-center gap-1" style={{ fontFamily: 'Chivo, sans-serif' }}>4.4<Star className="w-4 h-4 fill-amber-400 text-amber-400" /></p>
                <p className="text-[11px] uppercase tracking-wider text-white/70 mt-1">Avg Google</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-white/60">
            <p>New Brunswick &amp; Newfoundland</p>
            <p>© 2026 Elmwood Groups</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="lg:col-span-5 flex items-center justify-center p-6 sm:p-10 relative">
        <div className="lg:hidden absolute inset-x-0 top-0 h-56 overflow-hidden">
          <img src={BRAND_PHOTOS.front} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#3E2418]/70 via-[#3E2418]/60 to-[#FDFBF7]" />
        </div>
        <div className="w-full max-w-md relative">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center pt-8">
            <img src={AW_LOGO} alt="A&W" className="w-12 h-12 drop-shadow-lg" />
            <span className="text-2xl font-black text-white drop-shadow-md" style={{ fontFamily: 'Chivo, sans-serif' }}>Elmwood</span>
          </div>

          <div className="bg-white rounded-3xl shadow-[0_24px_60px_-20px_rgba(62,36,24,0.25)] p-8 sm:p-10 border border-orange-100/60 animate-slide-up">
            {/* Set Password Mode */}
            {mode === 'setPassword' ? (
              <>
                <div className="mb-8 text-center">
                  <div className="w-16 h-16 bg-[#F26522] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-orange-500/30">
                    <KeyRound className="w-8 h-8 text-white" />
                  </div>
                  <h1 className="text-2xl font-black text-[#3E2418] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Welcome to the team</h1>
                  <p className="mt-2 text-slate-500">Set up your password to get started</p>
                  {preregisteredUser?.store_name && (
                    <div className="mt-3 inline-flex items-center gap-2 bg-orange-50 text-orange-700 px-4 py-2 rounded-full text-sm font-medium">
                      <Building2 className="w-4 h-4" />
                      {preregisteredUser.store_name}
                    </div>
                  )}
                </div>
                <form onSubmit={handleSetPassword} className="space-y-5" autoComplete="on">
                  <div className="space-y-2">
                    <Label className="text-slate-700">Email</Label>
                    <Input name="email" type="email" autoComplete="username" value={email} disabled className="bg-slate-50" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Your Name</Label>
                    <Input name="name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="How should we call you?" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Create Password</Label>
                    <Input name="new-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Confirm Password</Label>
                    <Input name="confirm-password" type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Type it again" required />
                  </div>
                  <Button type="submit" className="w-full bg-[#F26522] hover:bg-[#d9551a] text-white font-semibold py-6 rounded-full shadow-lg shadow-orange-500/25 btn-press transition-colors" disabled={loading}>
                    {loading ? 'Setting up...' : <>Let's Go! <ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full" onClick={() => { setMode('login'); setPreregisteredUser(null); }}>
                    Back to Login
                  </Button>
                </form>
              </>
            ) : mode === 'forgotPassword' ? (
              <>
                <div className="mb-8 text-center">
                  <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-slate-600" />
                  </div>
                  <h1 className="text-2xl font-black text-[#3E2418] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>{resetEmailSent ? 'Check your inbox' : 'Forgot password?'}</h1>
                  <p className="mt-2 text-slate-500">{resetEmailSent ? 'We sent you a reset link' : "No worries, we'll send you a reset link"}</p>
                </div>
                {resetEmailSent ? (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                      <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-green-700 text-sm">Check your email for the reset link!</p>
                    </div>
                    <Button variant="outline" className="w-full" onClick={() => { setMode('login'); setResetEmailSent(false); }}>Back to Login</Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-5" autoComplete="on">
                    <div className="space-y-2">
                      <Label className="text-slate-700">Email Address</Label>
                      <Input name="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required />
                    </div>
                    <Button type="submit" className="w-full bg-[#3E2418] hover:bg-[#5C2E1E] text-white py-6 rounded-full btn-press" disabled={loading}>
                      {loading ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                    <Button type="button" variant="ghost" className="w-full" onClick={() => setMode('login')}>Back to Login</Button>
                  </form>
                )}
              </>
            ) : (
              <>
                {pendingApproval && (
                  <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="google-pending-approval">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                      <div className="text-sm">
                        <p className="font-semibold text-amber-900">Access request sent</p>
                        <p className="text-amber-800 mt-1">
                          Thanks{pendingApproval.name ? `, ${pendingApproval.name}` : ''}! Your Google account <b>{pendingApproval.email}</b> is waiting for an Elmwood admin to approve it. We've emailed the admin team — once approved, just tap "Continue with Google" again.
                        </p>
                        <button type="button" onClick={() => setPendingApproval(null)} className="mt-2 text-xs text-amber-700 underline">Dismiss</button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="mb-8">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#F26522] font-semibold mb-2">{mode === 'login' ? 'Team portal' : 'New account'}</p>
                  <h1 className="text-3xl font-black text-[#3E2418] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>{mode === 'login' ? 'Welcome back' : 'Join the team'}</h1>
                  <p className="mt-2 text-slate-500">{mode === 'login' ? 'Sign in to manage your store' : 'Create your account to get started'}</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
                  {mode === 'register' && (
                    <div className="space-y-2">
                      <Label className="text-slate-700">Full Name</Label>
                      <Input name="name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-slate-700">Email</Label>
                    <Input name="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={mode === 'login' ? checkEmail : undefined} placeholder="your@elmwoodgroups.com" required data-testid="email-input" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label className="text-slate-700">Password</Label>
                      {mode === 'login' && (
                        <button type="button" onClick={() => setMode('forgotPassword')} className="text-xs text-[#F26522] hover:text-[#d9551a] font-medium">Forgot password?</button>
                      )}
                    </div>
                    <Input name="password" type="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="password-input" />
                  </div>
                  {mode === 'register' && (
                    <div className="space-y-2">
                      <Label className="text-slate-700">Your Role</Label>
                      <Select value={role} onValueChange={setRole}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manager">Store Manager</SelectItem>
                          <SelectItem value="supervisor">Supervisor</SelectItem>
                          <SelectItem value="employee">Team Member</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button type="submit" className="w-full bg-[#F26522] hover:bg-[#d9551a] text-white font-semibold py-6 rounded-full shadow-lg shadow-orange-500/25 btn-press transition-colors" disabled={loading}>
                    {loading ? 'Please wait...' : mode === 'login' ? <>Sign In <ArrowRight className="w-4 h-4 ml-2" /></> : 'Create Account'}
                  </Button>
                </form>
                {mode === 'login' && (
                  <div className="mt-5">
                    <div className="relative flex items-center my-4">
                      <div className="flex-1 border-t border-slate-200" />
                      <span className="px-3 text-xs uppercase tracking-wide text-slate-400">or</span>
                      <div className="flex-1 border-t border-slate-200" />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGoogleSignIn}
                      disabled={loading}
                      className="w-full py-6 rounded-full border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold flex items-center justify-center gap-3 btn-press"
                      data-testid="google-signin-btn"
                    >
                      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                      </svg>
                      Continue with Google
                    </Button>
                    <p className="mt-2 text-[11px] text-slate-400 text-center">New here? Sign in with Google and an admin will be asked to approve your access.</p>
                  </div>
                )}
                <div className="mt-6 text-center">
                  <button type="button" onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-sm text-slate-600 hover:text-[#F26522] transition-colors">
                    {mode === 'login' ? "New here? Create an account" : "Already have an account? Sign in"}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Help Section */}
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-400">Need help? Contact support@elmwoodgroups.com</p>
          </div>
        </div>
      </div>
    </div>
  );
}
